/**
 * Deploy-resilience: recover from stale code-split chunks after a deploy.
 *
 * Vite emits hashed chunk filenames (e.g. `PartnersPage-BwoRrgHv.js`). When a
 * new build is deployed, those hashes change and the old files are removed. Any
 * already-open browser tab still runs the *previous* app, so navigating to a
 * lazy-loaded route makes it fetch an old chunk that no longer exists — Vite
 * fires a `vite:preloadError` event and, if unhandled, re-throws, which React
 * Router surfaces as the "Unexpected Application Error" page.
 *
 * We handle it by reloading once so the tab picks up the current build. A short
 * time-window guard (sessionStorage) prevents a reload loop when the chunk is
 * genuinely unfetchable (e.g. the server is down) — in that case the error page
 * is the correct fallback rather than an infinite refresh.
 */

const RELOAD_GUARD_KEY = 'qfly:preload-reload-at';
/** Don't auto-reload more than once within this window (loop guard). */
const RELOAD_WINDOW_MS = 10_000;

export interface PreloadReloadDeps {
  readonly storage: Pick<Storage, 'getItem' | 'setItem'>;
  readonly reload: () => void;
  readonly now: () => number;
}

/**
 * Core handler (dependency-injected for testing). Prevents the default throw,
 * then reloads once unless we already reloaded within the guard window.
 * Returns whether a reload was triggered.
 */
export const handlePreloadError = (
  event: Pick<Event, 'preventDefault'>,
  deps: PreloadReloadDeps,
): boolean => {
  // Stop Vite from re-throwing (which would show the router error page).
  event.preventDefault();

  const last = Number(deps.storage.getItem(RELOAD_GUARD_KEY) ?? '0');
  const current = deps.now();
  if (Number.isFinite(last) && last > 0 && current - last < RELOAD_WINDOW_MS) {
    // Already reloaded very recently → the chunk is truly unavailable; don't
    // loop. Let the error surface so the user isn't stuck refreshing.
    return false;
  }

  deps.storage.setItem(RELOAD_GUARD_KEY, String(current));
  deps.reload();
  return true;
};

/**
 * Register the browser listener. Safe no-op when there is no `window`
 * (SSR / test environments that don't provide one).
 */
export const registerPreloadErrorReload = (): void => {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    handlePreloadError(event, {
      storage: window.sessionStorage,
      reload: () => window.location.reload(),
      now: () => Date.now(),
    });
  });
};
